import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useRecentlyViewed } from "@/contexts/RecentlyViewedContext";
import { useStore } from "@/contexts/StoreContext";
import QtyStepper from "@/components/customer/QtyStepper";
import ProductCard from "@/components/customer/ProductCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronDown, ShoppingCart, Truck, ShieldCheck, RotateCcw, Package, ZoomIn,
  Timer, Leaf, Store as StoreIcon, BadgeCheck, Tag, Sparkles, Info,
} from "lucide-react";

/**
 * Image zoom viewer. Desktop: hover cursor tracking magnifier.
 * Mobile: tap to open fullscreen modal.
 */
function GalleryImage({ src, alt, fallback }) {
  const wrapRef = useRef(null);
  const [zoom, setZoom] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [full, setFull] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const onMove = (e) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  return (
    <>
      <div
        ref={wrapRef}
        className="rounded-2xl border border-border/60 bg-white aspect-square overflow-hidden relative cursor-zoom-in select-none p-4 md:p-6"
        onMouseEnter={() => src && setZoom(true)}
        onMouseLeave={() => setZoom(false)}
        onMouseMove={onMove}
        onClick={() => src && setFull(true)}
        data-testid="pdp-gallery-main"
      >
        {src ? (
          <>
            {!loaded && <div className="absolute inset-0 shimmer" aria-hidden />}
            <img
              src={src}
              alt={alt}
              loading="lazy"
              onLoad={() => setLoaded(true)}
              className={`w-full h-full object-contain transition-all duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
              style={zoom ? {
                transform: `scale(2)`,
                transformOrigin: `${pos.x}% ${pos.y}%`,
              } : undefined}
            />
          </>
        ) : (
          fallback
        )}
        <div className="absolute bottom-3 right-3 rounded-full bg-white/95 shadow p-1.5 pointer-events-none border border-border/40">
          <ZoomIn className="w-4 h-4" />
        </div>
      </div>

      {full && (
        <div
          className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4"
          onClick={() => setFull(false)}
          data-testid="pdp-gallery-fullscreen"
        >
          <img src={src} alt={alt} className="max-w-full max-h-full object-contain" />
          <button className="absolute top-4 right-4 text-white/80 hover:text-white text-sm">Close ✕</button>
        </div>
      )}
    </>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { track } = useRecentlyViewed();
  const { etaMinutes, ui } = useStore();
  const { itemCount: cartCount = 0, subtotal: cartSubtotal = 0 } = useCart() || {};
  const pdpCfg = ui?.pdp || {};
  const soldByText   = pdpCfg.sold_by_text || "Sold by";
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [variantIdx, setVariantIdx] = useState(0);  // Sprint 14 #5 — variant selector state
  const [sellerName, setSellerName] = useState("");   // Multi-store: "Sold by" lookup

  useEffect(() => {
    if (!user) { setP(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setP(data);
        setSelectedImage(0);
        track(data.id); // Record in Recently Viewed
        if (data.store_id) {
          api.get("/stores/public/directory")
            .then((r) => {
              const match = (r.data.items || []).find((s) => s.id === data.store_id);
              if (match) setSellerName(match.name);
            })
            .catch(() => {});
        }
        if (data.category_id) {
          api.get(`/products?category_id=${data.category_id}&limit=8`)
            .then((r) => setRelated((r.data.items || [])
              .filter((x) => x.id !== id)
              .filter((x) => !/^TEST[_-]/i.test(x.name || ""))
              .slice(0, 6)))
            .catch(() => {});
        }
      } catch (e) { toast.error(errMsg(e, "Product not found")); }
    })();
  }, [id, user, track]);

  const gallery = useMemo(() => {
    if (!p) return [];
    // Backend currently exposes a single thumbnail_url. If a future images[]
    // field lands, we render it here without any backend change required.
    const arr = Array.isArray(p.images) && p.images.length > 0 ? p.images : [];
    if (p.thumbnail_url && !arr.includes(p.thumbnail_url)) arr.unshift(p.thumbnail_url);
    return arr;
  }, [p]);

  if (!user && p === false) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 text-center" data-testid="pdp-signin-cta">
        <h1 className="text-3xl font-display font-semibold">Sign in to view this product</h1>
        <p className="mt-2 text-muted-foreground">Live pricing and stock require an account.</p>
        <Button asChild size="lg" className="mt-6 btn-primary-glow"><Link to={`/login?redirect=/product/${id}`}>Sign in</Link></Button>
      </div>
    );
  }

  if (!p) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <Skeleton className="aspect-square rounded-2xl" />
          <div className="space-y-3">
            <Skeleton className="h-8 w-2/3" /><Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-6 w-24 mt-6" /><Skeleton className="h-12 w-40 mt-6" />
          </div>
        </div>
      </div>
    );
  }

  const discount = p.mrp && Number(p.mrp) > Number(p.selling_price)
    ? Math.round(((p.mrp - p.selling_price) / p.mrp) * 100) : 0;

  const mainSrc = gallery[selectedImage] || p.thumbnail_url || null;
  const fallback = (
    <div className="w-full h-full grid place-items-center text-8xl font-display text-primary/25 bg-secondary">
      {p.name?.slice(0, 1)}
    </div>
  );

  // Sprint 14 #5 — Variants. If the product has variants, we swap price/stock/SKU
  // for the selected variant and pass variant_id to the cart stepper.
  const hasRealVariants = Array.isArray(p.variants) && p.variants.length > 0;
  const variants = hasRealVariants
    ? p.variants
    : (p.weight ? [{ weight: p.weight, weight_unit: p.weight_unit || "g", sku: p.sku }] : []);
  const selectedVariant = variants[variantIdx] || variants[0] || null;
  const displayPrice   = hasRealVariants && selectedVariant?.selling_price != null ? Number(selectedVariant.selling_price) : Number(p.selling_price);
  const displayMrp     = hasRealVariants && selectedVariant?.mrp != null ? Number(selectedVariant.mrp) : Number(p.mrp || 0);
  const displaySku     = hasRealVariants && selectedVariant?.sku ? selectedVariant.sku : p.sku;
  const displayStock   = hasRealVariants && selectedVariant?.stock != null ? Number(selectedVariant.stock) : (typeof p.stock === "number" ? p.stock : null);
  const displayImage   = hasRealVariants && selectedVariant?.thumbnail_url ? selectedVariant.thumbnail_url : mainSrc;
  const displayDiscount = displayMrp > displayPrice ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100) : discount;
  const variantId      = hasRealVariants ? (selectedVariant?.id || selectedVariant?._id || null) : null;

  // Pass to QtyStepper: a product-with-variant-price shape (so ADD toasts show name).
  const cartProduct = { ...p, id: p.id, selling_price: displayPrice, name: hasRealVariants && selectedVariant ? `${p.name} (${selectedVariant.weight} ${selectedVariant.weight_unit || "g"})` : p.name };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 pb-32 md:pb-8" data-testid="pdp-page">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4" data-testid="pdp-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 md:gap-10">
        {/* Gallery */}
        <div>
          <div className="relative">
            <GalleryImage src={displayImage} alt={p.name} fallback={fallback} />
            {displayDiscount > 0 && (
              <Badge className="absolute top-4 left-4 bg-primary hover:bg-primary text-sm px-3 py-1 z-10">-{displayDiscount}% OFF</Badge>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2" data-testid="pdp-thumbs">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition ${selectedImage === i ? "border-primary" : "border-transparent hover:border-border"}`}
                  data-testid={`pdp-thumb-${i}`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {p.brand_name && (
            <div className="text-[11px] uppercase tracking-[0.14em] text-primary font-bold" data-testid="pdp-brand">
              {p.brand_name}
            </div>
          )}
          <h1 className="text-2xl md:text-[32px] font-display font-semibold mt-1 leading-[1.15] tracking-tight" data-testid="pdp-title">{p.name}</h1>
          {p.weight && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center rounded-md bg-secondary/70 text-foreground/80 text-[12px] font-semibold px-2 py-0.5" data-testid="pdp-weight-chip">
                {p.weight} {p.weight_unit || "g"}
              </span>
            </div>
          )}

          {variants.length > 0 && (
            <div className="mt-5" data-testid="pdp-variants">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {hasRealVariants ? "Select size / pack" : "Pack size"}
              </div>
              <div className="flex flex-wrap gap-2">
                {variants.map((v, i) => {
                  const vp    = hasRealVariants && v.selling_price != null ? Number(v.selling_price) : Number(p.selling_price);
                  const stock = v.stock != null ? Number(v.stock) : null;
                  const oos   = stock === 0;
                  const active = i === variantIdx;
                  return (
                    <button
                      key={v.id || v.sku || i}
                      disabled={oos}
                      onClick={() => setVariantIdx(i)}
                      className={`px-3 py-2 rounded-lg border text-sm transition text-left leading-tight ${
                        active ? "border-primary bg-primary/5 text-primary shadow-sm" :
                        oos    ? "border-border bg-secondary/40 text-muted-foreground cursor-not-allowed" :
                                 "border-border hover:border-primary/40"
                      }`}
                      data-testid={`pdp-variant-${i}`}
                    >
                      <div className="font-semibold">{v.weight} {v.weight_unit || "g"}</div>
                      <div className="text-[11px] font-mono opacity-80">{money(vp)}{oos ? " · out" : ""}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Price hierarchy */}
          <div className="mt-5">
            <div className="flex items-baseline gap-2 gap-y-1.5 flex-wrap">
              <div className="text-[28px] md:text-[36px] font-bold text-foreground tabular-nums leading-none" data-testid="pdp-price">{money(displayPrice)}</div>
              {displayDiscount > 0 && (
                <>
                  <div className="text-base text-muted-foreground line-through tabular-nums" data-testid="pdp-mrp">{money(displayMrp)}</div>
                  <span className="inline-flex items-center rounded-md bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2 py-0.5 tracking-wide" data-testid="pdp-savings">
                    SAVE {money(displayMrp - displayPrice)} ({displayDiscount}%)
                  </span>
                </>
              )}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">Inclusive of all taxes</div>
            {displayStock !== null && displayStock > 0 && displayStock <= 5 && (
              <div className="mt-1.5 text-xs text-amber-700 font-semibold" data-testid="pdp-low-stock">Only {displayStock} left in stock</div>
            )}
          </div>

          {/* Premium delivery card */}
          <div className="mt-4 rounded-xl border bg-gradient-to-r from-primary/[0.04] to-transparent px-3 py-2.5 flex items-center gap-3" data-testid="pdp-delivery-card">
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
              <Timer className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold leading-tight">{pdpCfg.delivery_message || `Delivers in ${etaMinutes || 15} minutes`}</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">Fast dispatch from your Babu Store</div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-1 shrink-0">
              <Leaf className="w-2.5 h-2.5" /> FRESH
            </span>
          </div>

          {/* Blinkit-style FREE DELIVERY info box */}
          <div
            className="mt-3 rounded-xl border px-3 py-2.5 flex items-center gap-3"
            style={{ background: "hsl(var(--brand-green-soft))", borderColor: "hsl(var(--brand-green) / 0.25)" }}
            data-testid="pdp-free-delivery-box"
          >
            <div
              className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
              style={{ background: "hsl(var(--brand-green) / 0.14)", color: "hsl(var(--brand-green))" }}
            >
              <Truck className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-sm font-bold" style={{ color: "hsl(var(--brand-green-deep))" }}>Free delivery on orders above ₹499</div>
              <div className="mt-0.5 text-[11px] text-foreground/70">Add more items to unlock free doorstep delivery.</div>
            </div>
          </div>

          {/* Sprint 58 — Desktop add-to-cart: no separate "Go to cart" (MiniCart handles that). */}
          <div className="hidden md:flex mt-5 gap-3 items-center">
            <QtyStepper
              product={cartProduct}
              variantId={variantId}
              size="lg"
              testIdPrefix="pdp"
              addLabel="ADD"
              style={{ background: "hsl(var(--brand-green))", color: "#ffffff", border: "none", borderRadius: "12px" }}
            />
          </div>

          {/* Offer coupon card — dashed border, ticket-like */}
          {pdpCfg.offer_text && (
            <div
              className="mt-5 rounded-xl border-2 border-dashed border-primary/40 bg-primary/[0.04] px-3 py-2.5 flex items-center gap-3"
              data-testid="pdp-offer"
            >
              <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center shrink-0">
                <Tag className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-primary font-bold">Coupon offer</div>
                <div className="text-sm font-semibold text-foreground leading-tight">{pdpCfg.offer_text}</div>
              </div>
            </div>
          )}

          {/* Sold by — premium store card */}
          {sellerName && (
            <div className="mt-5 rounded-xl border bg-card px-3 py-2.5 flex items-center gap-3" data-testid="pdp-sold-by">
              <div className="w-9 h-9 rounded-lg bg-secondary text-foreground/80 grid place-items-center shrink-0">
                <StoreIcon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{soldByText}</div>
                <div className="text-sm font-semibold leading-tight truncate flex items-center gap-1">
                  {sellerName}
                  <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
                </div>
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 shrink-0">Verified</span>
            </div>
          )}

          {/* Product highlights — trust badge triad */}
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Why shop with us</div>
            <div className="grid grid-cols-3 gap-2" data-testid="pdp-highlights">
              {(Array.isArray(pdpCfg.trust_badges) && pdpCfg.trust_badges.length > 0
                ? pdpCfg.trust_badges.map((label, i) => ({ icon: [Truck, ShieldCheck, RotateCcw][i % 3], label }))
                : [
                    { icon: Truck, label: "30-min delivery" },
                    { icon: ShieldCheck, label: "100% authentic" },
                    { icon: RotateCcw, label: "Easy 24-hr returns" },
                  ]).map((f) => (
                <div key={f.label} className="rounded-xl border bg-card px-2.5 py-3 flex flex-col items-center gap-1.5 text-center">
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                    <f.icon className="w-4 h-4" />
                  </div>
                  <span className="text-[11px] font-semibold leading-tight text-foreground/85">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {p.description && (
            <div className="mt-6">
              <div className="font-semibold mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" />About this product</div>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{p.description}</p>
            </div>
          )}

          {/* Sprint 58d — Blinkit-style Product Information (collapsed by default).
              Customer-useful fields only: NO SKU, NO barcode, NO brand_id, NO
              internal DB fields. Accordion animates open smoothly. Hidden entirely
              when no fields are populated. */}
          {(p.weight || p.brand_name || p.country_of_origin || p.shelf_life ||
            (p.tax_rate != null && Number(p.tax_rate) > 0) || p.manufacturer ||
            p.storage_instructions || p.ingredients) && (
          <details className="mt-6 group rounded-2xl border border-border/60 bg-card overflow-hidden [&_summary::-webkit-details-marker]:hidden" data-testid="pdp-product-info">
            <summary className="list-none cursor-pointer select-none flex items-center justify-between px-4 py-3.5 hover:bg-secondary/30 transition rounded-2xl">
              <span className="inline-flex items-center gap-2 font-semibold text-sm">
                <Info className="w-4 h-4 text-primary" /> Product information
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-300 group-open:rotate-180" />
            </summary>
            <div className="px-4 pb-4 pt-1 grid grid-cols-2 gap-x-4 gap-y-3 text-xs animate-in fade-in slide-in-from-top-1 duration-200">
              {p.weight && (
                <div>
                  <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Net weight</div>
                  <div className="font-medium mt-0.5">{p.weight} {p.weight_unit || "g"}</div>
                </div>
              )}
              {p.brand_name && (
                <div>
                  <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Brand</div>
                  <div className="font-medium mt-0.5">{p.brand_name}</div>
                </div>
              )}
              {p.country_of_origin && (
                <div>
                  <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Country of origin</div>
                  <div className="font-medium mt-0.5">{p.country_of_origin}</div>
                </div>
              )}
              {p.shelf_life && (
                <div>
                  <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Shelf life</div>
                  <div className="font-medium mt-0.5">{p.shelf_life}</div>
                </div>
              )}
              {p.tax_rate != null && Number(p.tax_rate) > 0 && (
                <div>
                  <div className="text-muted-foreground uppercase tracking-wider text-[10px]">GST</div>
                  <div className="font-medium mt-0.5">{Number(p.tax_rate)}%</div>
                </div>
              )}
              {p.manufacturer && (
                <div className="col-span-2">
                  <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Manufacturer</div>
                  <div className="font-medium mt-0.5 leading-snug">{p.manufacturer}</div>
                </div>
              )}
              {p.storage_instructions && (
                <div className="col-span-2">
                  <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Storage instructions</div>
                  <div className="font-medium mt-0.5 leading-snug">{p.storage_instructions}</div>
                </div>
              )}
              {p.ingredients && (
                <div className="col-span-2">
                  <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Ingredients</div>
                  <div className="font-medium mt-0.5 leading-snug">{p.ingredients}</div>
                </div>
              )}
            </div>
          </details>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-14" data-testid="pdp-related">
          <h2 className="text-xl md:text-2xl font-display font-semibold mb-4">You may also like</h2>
          <div className="snap-rail rail-fade-mask md:grid md:grid-cols-6 md:gap-4">
            {related.map((r) => (
              <div key={r.id} className="snap-start shrink-0 w-[44vw] max-w-[180px] md:w-auto md:max-w-none" data-testid={`pdp-related-${r.id}`}>
                <ProductCard p={r} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sprint 58e — Premium Blinkit-style sticky footer.
            Container: white, rounded-top 20 px, soft top shadow. Only the ADD
            TO CART button (and stepper) carries the red brand accent. */}
      <div
        className="md:hidden fixed bottom-16 left-0 right-0 z-30 slide-up-in"
        data-testid="pdp-sticky-bar"
      >
        <div className="bg-white rounded-t-[20px] shadow-[0_-10px_28px_-10px_rgba(0,0,0,0.14)] overflow-hidden">
          {cartCount > 0 && (
            <Link
              to="/cart"
              className="flex items-center justify-between gap-2 px-4 py-2.5 transition hover:brightness-105"
              style={{ background: "hsl(var(--brand-green))", color: "#fff" }}
              data-testid="pdp-view-cart-pill"
            >
              <div className="min-w-0 flex items-center gap-2 leading-tight">
                <ShoppingCart className="w-4 h-4 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[12.5px] font-bold">
                    {cartCount} item{cartCount === 1 ? "" : "s"} in cart
                    <span className="opacity-80 font-medium"> · </span>
                    <span className="tabular-nums">{money(cartSubtotal)}</span>
                  </div>
                </div>
              </div>
              <span className="text-[12px] font-bold uppercase tracking-wider inline-flex items-center gap-1">
                View Cart <ChevronLeft className="w-3.5 h-3.5 rotate-180" />
              </span>
            </Link>
          )}
          <div className="px-4 py-3 flex items-center gap-3">
            <div className="min-w-0 flex-1 leading-tight">
              <div className="flex items-baseline gap-2 flex-wrap">
                <div className="text-[22px] font-bold text-foreground tabular-nums" data-testid="pdp-sticky-price">{money(displayPrice)}</div>
                {displayDiscount > 0 && (
                  <div className="text-[12px] text-muted-foreground tabular-nums">
                    MRP <span className="line-through">{money(displayMrp)}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                {displayDiscount > 0 && (
                  <span className="inline-flex items-center text-[11px] font-bold text-emerald-700 tracking-wide">
                    {displayDiscount}% OFF
                  </span>
                )}
                {p.weight && (
                  <span className="text-[10.5px] text-muted-foreground truncate">
                    {displayDiscount > 0 ? "· " : ""}{p.weight} {p.weight_unit || "g"} · Incl. taxes
                  </span>
                )}
              </div>
            </div>
            <QtyStepper
              product={cartProduct}
              variantId={variantId}
              size="md"
              testIdPrefix="pdp-mobile"
              addLabel="ADD"
              style={{ background: "hsl(var(--primary))", color: "#ffffff", border: "none", borderRadius: "10px" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
